import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dialog, Field, Input, RadioGroup } from "@/components/ui";
import { SocialNetworkProfileServices } from "@/services/bedrock";
import { CardAlert, getErrorMessage, useSocialProfiles } from "@/utils";
import { LifeBuoy, Loader2 } from "lucide-react";
import { SocialAlert } from "./SocialAlert";
import { SocialHelpGuide } from "./SocialHelpGuide";
import { ZillowHelpVariant } from "./shared";

export type ZillowProfileType = "agent" | "lender";
type ZillowConnectStep = "type" | "agent-url" | "lender-import";

const ZILLOW_PROFILE_URL_REGEX = /^https?:\/\/.+zillow\.com\/profile\/.+/i;
const DEFAULT_PROFILE_URL = "https://www.zillow.com/profile/";

interface Props {
  open: boolean;
  userId: string | number;
  onOpenChange: (open: boolean) => void;
  onConnectComplete?: () => void;
}

export function ZillowConnectDialog({ open, userId, onOpenChange, onConnectComplete }: Props) {
  const { refetchProfile } = useSocialProfiles(userId);
  const [step, setStep] = useState<ZillowConnectStep>("type");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAlert, setSubmitAlert] = useState<CardAlert>();
  const [showHelp, setShowHelp] = useState(false);
  const [helpVariant, setHelpVariant] = useState<ZillowHelpVariant>("lender");

  useEffect(() => {
    if (open) {
      setStep("type");
      setIsSubmitting(false);
      setSubmitAlert(undefined);
      setShowHelp(false);
      setHelpVariant("lender");
    }
  }, [open]);

  const close = useCallback(() => {
    if (isSubmitting) return;
    onOpenChange(false);
  }, [isSubmitting, onOpenChange]);

  const goToStep = useCallback((nextStep: ZillowConnectStep) => {
    setSubmitAlert(undefined);
    setStep(nextStep);
  }, []);

  const openHelp = useCallback((variant: ZillowHelpVariant) => {
    setHelpVariant(variant);
    setShowHelp(true);
  }, []);

  const connectAgent = useCallback(
    async (profileUrl: string) => {
      setSubmitAlert(undefined);
      setIsSubmitting(true);
      try {
        const profile = await SocialNetworkProfileServices.createProfile(userId, {
          socialNetworkId: "ZL",
          profileType: "agent",
          profileUrl,
        });
        onOpenChange(false);
        await refetchProfile(profile.id);
        onConnectComplete?.();
      } catch (e: unknown) {
        console.error(e);
        setSubmitAlert({
          type: "error",
          message: "Unable to connect Zillow profile. Please try again.",
          description: getErrorMessage(e),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [userId, refetchProfile, onConnectComplete, onOpenChange]
  );

  const connectLender = useCallback(
    async (nmlsId: string, companyName?: string) => {
      setSubmitAlert(undefined);
      setIsSubmitting(true);
      try {
        const profile = await SocialNetworkProfileServices.createProfile(userId, {
          socialNetworkId: "ZL",
          profileType: "lender",
          nmlsId,
          companyName,
        });
        onOpenChange(false);
        await refetchProfile(profile.id);
        onConnectComplete?.();
      } catch (e: unknown) {
        console.error(e);
        setSubmitAlert({
          type: "error",
          message: "Unable to connect Zillow lender. Please try again.",
          description: getErrorMessage(e),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [userId, refetchProfile, onConnectComplete, onOpenChange]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && !isSubmitting) close();
    },
    [close, isSubmitting]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div className={showHelp ? "hidden" : "contents"}>
        {step === "type" && <TypeStep onCancel={close} onContinue={goToStep} onShowHelp={openHelp} />}
        {step === "agent-url" && (
          <AgentUrlStep
            isSubmitting={isSubmitting}
            submitAlert={submitAlert}
            onClearAlert={() => setSubmitAlert(undefined)}
            onCancel={close}
            onConfirm={connectAgent}
            onShowHelp={() => openHelp("agent")}
          />
        )}
        {step === "lender-import" && (
          <LenderImportStep
            isSubmitting={isSubmitting}
            submitAlert={submitAlert}
            onClearAlert={() => setSubmitAlert(undefined)}
            onCancel={close}
            onConfirm={connectLender}
            onShowHelp={() => openHelp("lender")}
          />
        )}
      </div>
      {showHelp && (
        <Dialog.Content className="max-h-[min(70vh,32rem)] overflow-y-auto">
          <SocialHelpGuide networkId="ZL" zillowVariant={helpVariant} embedded onBack={() => setShowHelp(false)} />
        </Dialog.Content>
      )}
    </Dialog>
  );
}

function DialogStepFooter({ onShowHelp, children }: { onShowHelp: () => void; children: React.ReactNode }) {
  return (
    <Dialog.Footer className="flex-row items-center justify-between gap-2 sm:justify-between">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onShowHelp}
        className="gap-1.5 text-slate-600 hover:text-slate-900"
      >
        <LifeBuoy size={14} className="text-primary" />
        Need help?
      </Button>
      <div className="flex flex-row gap-2">{children}</div>
    </Dialog.Footer>
  );
}

function TypeStep({
  onCancel,
  onContinue,
  onShowHelp,
}: {
  onCancel: () => void;
  onContinue: (step: ZillowConnectStep) => void;
  onShowHelp: (variant: ZillowProfileType) => void;
}) {
  const [profileType, setProfileType] = useState<ZillowProfileType>("lender");

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>Connect Zillow</Dialog.Title>
        <Dialog.Description>Choose whether you are connecting an agent or lender profile.</Dialog.Description>
      </Dialog.Header>
      <Dialog.Content>
        <RadioGroup
          value={profileType}
          onValueChange={value => setProfileType(value as ZillowProfileType)}
          className="gap-2"
        >
          <RadioGroup.Button
            value="lender"
            id="zillow-profile-type-lender"
            label={
              <div className="flex flex-col items-start gap-0 text-left text-base">
                <span className="font-semibold">Lender</span>
                <span className="text-sm text-muted-foreground">
                  Connect with your NMLS ID to import lender reviews from Zillow.
                </span>
              </div>
            }
          />
          <RadioGroup.Button
            value="agent"
            id="zillow-profile-type-agent"
            label={
              <div className="flex flex-col items-start gap-0 text-left text-base">
                <span className="font-semibold">Agent</span>
                <span className="text-sm text-muted-foreground">
                  Connect a zillow.com/profile URL to import agent reviews from Zillow.
                </span>
              </div>
            }
          />
        </RadioGroup>
      </Dialog.Content>
      <DialogStepFooter onShowHelp={() => onShowHelp(profileType)}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onContinue(profileType === "agent" ? "agent-url" : "lender-import")}>Continue</Button>
      </DialogStepFooter>
    </>
  );
}

function AgentUrlStep({
  isSubmitting,
  submitAlert,
  onClearAlert,
  onCancel,
  onConfirm,
  onShowHelp,
}: {
  isSubmitting?: boolean;
  submitAlert?: CardAlert;
  onClearAlert: () => void;
  onCancel: () => void;
  onConfirm: (profileUrl: string) => void | Promise<void>;
  onShowHelp: () => void;
}) {
  const [profileUrl, setProfileUrl] = useState(DEFAULT_PROFILE_URL);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setProfileUrl(DEFAULT_PROFILE_URL);
    setTouched(false);
  }, []);

  const trimmed = profileUrl.trim();
  const isValid = useMemo(() => {
    if (!trimmed) return false;
    return ZILLOW_PROFILE_URL_REGEX.test(trimmed);
  }, [trimmed]);

  const errorText = useMemo(() => {
    if (!touched || !trimmed || isValid) return undefined;
    return "Please enter a valid Zillow profile URL.";
  }, [touched, trimmed, isValid]);

  const handleConnect = useCallback(() => {
    setTouched(true);
    if (!isValid) return;
    onConfirm(trimmed);
  }, [isValid, onConfirm, trimmed]);

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>Connect Zillow</Dialog.Title>
        <Dialog.Description>Paste the public URL from your Zillow agent profile page.</Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-4 max-h-[min(60vh,28rem)] overflow-y-auto">
        <Field>
          <Field.Label htmlFor="zillow-profile-url">Profile URL</Field.Label>
          <Input
            id="zillow-profile-url"
            value={profileUrl}
            onChange={e => {
              setProfileUrl(e.target.value);
              if (!touched) setTouched(true);
            }}
            placeholder="https://www.zillow.com/profile/your-name"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="url"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConnect();
              }
            }}
          />
          {errorText && <p className="text-sm text-destructive">{errorText}</p>}
        </Field>
        <p className="text-sm text-muted-foreground">Example: https://www.zillow.com/profile/your-name</p>
        {submitAlert && <SocialAlert alert={submitAlert} clearAlert={onClearAlert} onClickAction={() => undefined} />}
      </Dialog.Content>
      <DialogStepFooter onShowHelp={onShowHelp}>
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConnect} disabled={!isValid || isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" size={16} />
              Connecting...
            </>
          ) : (
            "Connect"
          )}
        </Button>
      </DialogStepFooter>
    </>
  );
}

function LenderImportStep({
  isSubmitting,
  submitAlert,
  onClearAlert,
  onCancel,
  onConfirm,
  onShowHelp,
}: {
  isSubmitting?: boolean;
  submitAlert?: CardAlert;
  onClearAlert: () => void;
  onCancel: () => void;
  onConfirm: (nmlsId: string, companyName?: string) => void | Promise<void>;
  onShowHelp: () => void;
}) {
  const [nmlsId, setNmlsId] = useState("");
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    setNmlsId("");
    setCompanyName("");
  }, []);

  const isNmlsValid = useMemo(() => /^\d+$/.test(nmlsId), [nmlsId]);

  const handleConnect = useCallback(() => {
    if (!isNmlsValid) return;
    onConfirm(nmlsId, companyName || undefined);
  }, [isNmlsValid, onConfirm, companyName, nmlsId]);

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>Connect Zillow lender</Dialog.Title>
        <Dialog.Description>
          Enter your NMLS ID to connect your Zillow lender profile and import reviews. Add a company name if you are
          connecting on behalf of an entire lender company.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-4 max-h-[min(60vh,28rem)] overflow-y-auto">
        <Field>
          <Field.Label htmlFor="zillow-lender-nmls-id">NMLS ID</Field.Label>
          <Input
            id="zillow-lender-nmls-id"
            value={nmlsId}
            onChange={e => setNmlsId(e.target.value)}
            placeholder="123456"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="numeric"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConnect();
              }
            }}
          />
          {isNmlsValid || <p className="text-sm text-destructive">Please enter a valid numeric NMLS ID.</p>}
        </Field>
        <Field>
          <Field.Label htmlFor="zillow-lender-company-name">Company name (optional)</Field.Label>
          <Input
            id="zillow-lender-company-name"
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
            placeholder="Acme Mortgage"
            autoCapitalize="words"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConnect();
              }
            }}
          />
          <p className="text-sm text-muted-foreground">
            Required for institutional lenders when multiple companies share the same NMLS ID.
          </p>
        </Field>
        {submitAlert && <SocialAlert alert={submitAlert} clearAlert={onClearAlert} onClickAction={() => undefined} />}
      </Dialog.Content>
      <DialogStepFooter onShowHelp={onShowHelp}>
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConnect} disabled={!isNmlsValid || isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" size={16} />
              Connecting...
            </>
          ) : (
            "Connect"
          )}
        </Button>
      </DialogStepFooter>
    </>
  );
}
